import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { showError, showSuccess } from "../../../redux/counter/counterSlice";
import { useAppDispatch } from "../../../redux/store";
import {
    useVrboJoinNewListingDiscountMutation,
    useVrboQueryPromotionEligibilityMutation,
    useVrboQueryPropertyPromotionsMutation,
    useVrboUpdatePromotionMutation,
    useVrboWithdrawNewListingDiscountMutation,
} from "../../../services/bClientApi";
import JSONData from "../../JSONData/JSONData";
import { ConfirmModal } from "../../Modals/confirmModal";
import VrboPromotionFormModal from "../promotions/VrboPromotionFormModal";
import { summarizeDiscount } from "../promotions/vrboPromotionHelpers";
import { VrboPromotion } from "../promotions/vrboPromotionTypes";
import useVrboAction from "../useVrboAction";
import VrboActionRow from "../VrboActionRow";

const PAGE_SIZE = 25

type StatusFilter = '' | 'ACTIVE' | 'INACTIVE'

const sellStatusVariant: Record<string, string> = {
    CURRENT: 'success',
    FUTURE: 'info',
    EXPIRED: 'secondary',
}

export default function VrboPromotionsSection(props: { propertyId: string }) {
    const { propertyId } = props

    const dispatch = useAppDispatch()

    const [queryEligibility, { isLoading: isLoadingEligibility }] = useVrboQueryPromotionEligibilityMutation()
    const [queryPropertyPromotions, { isLoading: isLoadingPromotions }] = useVrboQueryPropertyPromotionsMutation()
    const [updatePromotion, { isLoading: isUpdatingPromotion }] = useVrboUpdatePromotionMutation()
    const [joinNewListingDiscount] = useVrboJoinNewListingDiscountMutation()
    const [withdrawNewListingDiscount] = useVrboWithdrawNewListingDiscountMutation()

    const [eligibility, setEligibility] = useState<any>(null)
    const [eligibilityError, setEligibilityError] = useState<any>(null)

    const [promotions, setPromotions] = useState<VrboPromotion[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [cursor, setCursor] = useState<string | null>(null)
    const [promotionsError, setPromotionsError] = useState<any>(null)
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('')

    const [formPromotion, setFormPromotion] = useState<VrboPromotion | null>(null)
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [statusTarget, setStatusTarget] = useState<VrboPromotion | null>(null)

    // ignores responses from a request that was superseded by a newer one
    const latestListRequest = useRef(0)

    const loadEligibility = useCallback(async () => {
        if (!propertyId) return
        try {
            const response = await queryEligibility(propertyId).unwrap()
            setEligibility(response?.response?.promotionEligibility?.newListingDiscountEligibility || null)
            setEligibilityError(null)
        } catch (error: any) {
            console.error('Error querying VRBO promotion eligibility:', error)
            setEligibilityError(error?.data || error)
        }
    }, [propertyId, queryEligibility])

    const loadPromotions = useCallback(async (nextCursor?: string | null) => {
        if (!propertyId) return
        const requestId = ++latestListRequest.current
        try {
            const response = await queryPropertyPromotions({
                propertyId,
                filters: statusFilter ? { status: statusFilter } : undefined,
                cursor: nextCursor || undefined,
                limit: PAGE_SIZE,
            }).unwrap()
            if (requestId !== latestListRequest.current) return
            const page = response?.response?.property?.promotionsV2
            const elements: VrboPromotion[] = page?.elements || []
            setPromotions(previous => nextCursor ? [...previous, ...elements] : elements)
            setTotalCount(page?.totalCount ?? elements.length)
            setCursor(page?.cursor || null)
            setPromotionsError(null)
        } catch (error: any) {
            if (requestId !== latestListRequest.current) return
            console.error('Error querying VRBO property promotions:', error)
            setPromotionsError(error?.data || error)
        }
    }, [propertyId, queryPropertyPromotions, statusFilter])

    useEffect(() => {
        loadEligibility()
    }, [loadEligibility])

    useEffect(() => {
        loadPromotions()
    }, [loadPromotions])

    const refreshAll = () => {
        loadEligibility()
        loadPromotions()
    }

    const enabled = !!propertyId
    const joinAction = useVrboAction(
        () => joinNewListingDiscount(propertyId).unwrap(),
        { errorLabel: 'joining VRBO new listing discount', enabled, onSuccess: refreshAll },
    )
    const withdrawAction = useVrboAction(
        () => withdrawNewListingDiscount(propertyId).unwrap(),
        { errorLabel: 'withdrawing VRBO new listing discount', enabled, onSuccess: refreshAll },
    )

    const openCreate = () => {
        setFormPromotion(null)
        setIsFormOpen(true)
    }

    const openEdit = (promotion: VrboPromotion) => {
        setFormPromotion(promotion)
        setIsFormOpen(true)
    }

    const onConfirmToggleStatus = async () => {
        if (!statusTarget) return
        const nextStatus = statusTarget.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
        try {
            await updatePromotion({
                propertyId,
                promotionId: statusTarget.id,
                promotion: { status: nextStatus },
            }).unwrap()
            dispatch(showSuccess(`Promotion ${nextStatus === 'ACTIVE' ? 'activated' : 'deactivated'}`))
            loadPromotions()
        } catch (error: any) {
            console.error('Error updating VRBO promotion status:', error)
            dispatch(showError(error?.data?.error || 'Failed to update promotion status'))
        }
        setStatusTarget(null)
    }

    const isIneligible = eligibility?.eligibilityStatus === 'INELIGIBLE'
    const ineligibilityReasons: string[] = eligibility?.ineligibilityReasons || []
    const validBookingRange = eligibility?.validBookingDateRange

    return (
        <div>
            <Card className="mb-3">
                <Card.Body>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <Card.Title as="h6" className="mb-0">New Listing Discount</Card.Title>
                        <Button size="sm" variant="outline-secondary" onClick={loadEligibility} disabled={isLoadingEligibility}>
                            {isLoadingEligibility ? 'Checking...' : 'Check Eligibility'}
                        </Button>
                    </div>

                    {eligibilityError && (
                        <JSONData data={eligibilityError} title="Error querying eligibility" onClose={() => setEligibilityError(null)} />
                    )}

                    {eligibility && (
                        <div className="mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <Badge bg={isIneligible ? 'secondary' : 'success'}>{eligibility.eligibilityStatus}</Badge>
                                <small className="text-muted">Remaining days: {eligibility.remainingDays}</small>
                            </div>
                            {validBookingRange && (
                                <small className="text-muted d-block mt-1">
                                    Valid booking window: {validBookingRange.from} to {validBookingRange.to}
                                </small>
                            )}
                            {ineligibilityReasons.length > 0 && (
                                <ul className="small text-muted mt-1 mb-0">
                                    {ineligibilityReasons.map(reason => <li key={reason}>{reason}</li>)}
                                </ul>
                            )}
                        </div>
                    )}

                    <VrboActionRow
                        action={joinAction}
                        label="Join New Listing Discount"
                        busyLabel="Joining..."
                        resultTitle="Join New Listing Discount Response"
                        variant="success"
                        disabled={isIneligible}
                        confirm={{
                            title: 'Join new listing discount?',
                            message: 'This will enroll this property in the VRBO new listing discount.',
                        }}
                    />
                    <VrboActionRow
                        action={withdrawAction}
                        label="Withdraw New Listing Discount"
                        busyLabel="Withdrawing..."
                        resultTitle="Withdraw New Listing Discount Response"
                        variant="warning"
                        confirm={{
                            title: 'Withdraw new listing discount?',
                            message: 'This will remove this property from the VRBO new listing discount.',
                        }}
                    />
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                        <Card.Title as="h6" className="mb-0">Promotions</Card.Title>
                        <div className="d-flex align-items-center gap-2">
                            <Form.Select
                                size="sm"
                                value={statusFilter}
                                onChange={(e: any) => setStatusFilter(e.target.value)}
                                aria-label="Filter promotions by status"
                            >
                                <option value="">All</option>
                                <option value="ACTIVE">Active</option>
                                <option value="INACTIVE">Inactive</option>
                            </Form.Select>
                            <Button size="sm" variant="outline-secondary" onClick={() => loadPromotions()} disabled={isLoadingPromotions}>
                                Refresh
                            </Button>
                            <Button size="sm" onClick={openCreate}>
                                New Promotion
                            </Button>
                        </div>
                    </div>

                    {promotionsError && (
                        <div className="mb-3">
                            <JSONData data={promotionsError} title="Error querying promotions" onClose={() => setPromotionsError(null)} />
                        </div>
                    )}

                    {isLoadingPromotions && !promotions.length ? (
                        <div className="text-center"><Spinner animation="border" size="sm" /></div>
                    ) : promotions.length === 0 ? (
                        <Alert variant="light" className="mb-0">No promotions found.</Alert>
                    ) : (
                        <>
                            <Table size="sm" responsive hover>
                                <thead>
                                    <tr>
                                        <th>Promotion</th>
                                        <th>Status</th>
                                        <th>Discount</th>
                                        <th>Travel dates</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {promotions.map(promotion => (
                                        <tr key={promotion.id}>
                                            <td>
                                                <div>{promotion.name || promotion.promotionType}</div>
                                                <small className="text-muted">{promotion.promotionType}</small>
                                            </td>
                                            <td>
                                                <Badge bg={promotion.status === 'ACTIVE' ? 'success' : 'secondary'} className="me-1">
                                                    {promotion.status}
                                                </Badge>
                                                {promotion.sellStatus && (
                                                    <Badge bg={sellStatusVariant[promotion.sellStatus] || 'secondary'}>
                                                        {promotion.sellStatus}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>{summarizeDiscount(promotion)}</td>
                                            <td>
                                                {promotion.restrictions?.travelDateRange
                                                    ? `${promotion.restrictions.travelDateRange.from} - ${promotion.restrictions.travelDateRange.to}`
                                                    : '-'}
                                            </td>
                                            <td className="text-nowrap text-end">
                                                <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(promotion)}>
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant={promotion.status === 'ACTIVE' ? 'outline-warning' : 'outline-success'}
                                                    onClick={() => setStatusTarget(promotion)}
                                                    disabled={isUpdatingPromotion}
                                                >
                                                    {promotion.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <div className="d-flex align-items-center justify-content-between">
                                <small className="text-muted">Showing {promotions.length} of {totalCount}</small>
                                {cursor && (
                                    <Button size="sm" variant="outline-primary" onClick={() => loadPromotions(cursor)} disabled={isLoadingPromotions}>
                                        {isLoadingPromotions ? 'Loading...' : 'Load more'}
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            <VrboPromotionFormModal
                active={isFormOpen}
                propertyId={propertyId}
                promotion={formPromotion}
                onClose={() => setIsFormOpen(false)}
                onSaved={() => loadPromotions()}
            />

            <ConfirmModal
                active={!!statusTarget}
                title={statusTarget?.status === 'ACTIVE' ? 'Deactivate promotion?' : 'Activate promotion?'}
                message={statusTarget?.status === 'ACTIVE'
                    ? 'This promotion will stop applying to new bookings on VRBO.'
                    : 'This promotion will start applying to new bookings on VRBO.'}
                onClose={() => setStatusTarget(null)}
                onConfirm={onConfirmToggleStatus}
                disabled={isUpdatingPromotion}
            />
        </div>
    )
}
